import { createContext, useContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { signIn as redirectSignIn, signOut as redirectSignOut } from './msal';

export interface CurrentUser {
  actor_id: string;
  actor_email: string;
  actor_role: 'super_admin' | 'admin' | 'maker' | 'checker' | 'viewer';
  tenant_id: string;
}

interface AuthContextValue {
  user: CurrentUser | null;
  isLoading: boolean;
  /** True if /api/me returned a user OR a dev token is stored locally. */
  hasCredentials: boolean;
  /** True once the auth check has resolved (success or 401). */
  authReady: boolean;
  signIn: () => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  isLoading: true,
  hasCredentials: false,
  authReady: false,
  signIn: () => undefined,
  signOut: () => undefined,
});

/**
 * AuthProvider: probes /api/me on mount. The session cookie set by the
 * Azure OAuth callback is sent automatically (withCredentials: true on
 * the axios instance). 401 means the user isn't signed in.
 *
 * Dev fallback: if no cookie session, the api.ts interceptor also tries
 * a localStorage JWT — same code path, same /api/me.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, isError, isFetched } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<CurrentUser>('/api/me').then(r => r.data),
    staleTime: 5 * 60 * 1000,
    retry: false,  // 401 should not retry; just show login
  });

  const value: AuthContextValue = {
    user: data ?? null,
    isLoading,
    hasCredentials: !!data && !isError,
    authReady: isFetched,
    signIn: redirectSignIn,
    signOut: () => { void redirectSignOut(); },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

export function useCurrentUser(): CurrentUser | null {
  return useContext(AuthContext).user;
}

export function useHasRole(...roles: Array<CurrentUser['actor_role']>): boolean {
  const u = useCurrentUser();
  if (!u) return false;
  if (u.actor_role === 'admin' || u.actor_role === 'super_admin') return true;
  return roles.includes(u.actor_role);
}
